import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { CustomSelect } from "../components/CustomSelect";
import type { CustomSelectOption } from "../components/CustomSelect";
import { Shell } from "../components/Shell";
import { ApiError, tenants as tenantsApi } from "../lib/api";
import type {
  PanoramaResponse,
  PanoramaTopTenant,
  PanoramaTrendDay,
  Tenant,
  TenantStatus,
} from "../lib/api";
import { useAuth } from "../lib/auth-context";

const STATUS_LABEL: Record<string, string> = {
  trial: "En prueba",
  active: "Activa",
  suspended: "Suspendida",
  cancelled: "Cancelada",
};
const MES_NOMBRE: Record<string, string> = {
  "01": "Enero", "02": "Febrero", "03": "Marzo", "04": "Abril", "05": "Mayo",
  "06": "Junio", "07": "Julio", "08": "Agosto", "09": "Septiembre",
  "10": "Octubre", "11": "Noviembre", "12": "Diciembre",
};

function fmtMes(s: string): string {
  const [y, m] = s.split("-");
  return `${MES_NOMBRE[m ?? ""] ?? m ?? ""} ${y ?? ""}`;
}

function complianceCls(pct: number | null): string {
  if (pct === null) return "muted";
  if (pct >= 92) return "ok";
  if (pct >= 82) return "warn";
  return "danger";
}

export function DashboardPage() {
  const { user } = useAuth();
  const [data, setData] = useState<PanoramaResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [estado, setEstado] = useState<TenantStatus | "">("");
  const [tenantId, setTenantId] = useState("");
  const [allTenants, setAllTenants] = useState<Tenant[]>([]);

  useEffect(() => {
    tenantsApi
      .list({})
      .then((r) => setAllTenants(r.items))
      .catch(() => setAllTenants([]));
  }, []);

  useEffect(() => {
    setLoading(true);
    setError(null);
    tenantsApi
      .panorama({
        estado: estado || undefined,
        tenant_id: tenantId || undefined,
      })
      .then(setData)
      .catch((err) => setError(err instanceof ApiError ? err.message : "Error de red"))
      .finally(() => setLoading(false));
  }, [estado, tenantId]);

  const estadoOpts: ReadonlyArray<CustomSelectOption<string>> = [
    { value: "", label: "Todos los estados" },
    { value: "trial", label: "En prueba" },
    { value: "active", label: "Activas" },
    { value: "suspended", label: "Suspendidas" },
    { value: "cancelled", label: "Canceladas" },
  ];
  const tenantOpts: ReadonlyArray<CustomSelectOption<string>> = [
    { value: "", label: "Todas las alcaldías" },
    ...allTenants
      .slice()
      .sort((a, b) => a.nombre.localeCompare(b.nombre, "es"))
      .map((t) => ({
        value: t.id,
        label: t.nombre,
        hint: t.municipio ?? undefined,
      })),
  ];
  const filtrosActivos = Boolean(estado || tenantId);

  const trendMax = useMemo(() => {
    if (!data) return 1;
    let m = 0;
    for (const d of data.trend_30d) if (d.radicados > m) m = d.radicados;
    return Math.max(m, 1);
  }, [data]);

  return (
    <Shell>
      <section className="page">
        <div className="eyebrow">Plataforma · Panorama</div>
        <h1>Hola, {user?.nombre.split(" ")[0] ?? "admin"}.</h1>
        <p className="lede">
          {data
            ? `${data.tenants_total} alcaldía${data.tenants_total === 1 ? "" : "s"} · ${data.pqrsdf_total} PQRSDF acumulados. Última actualización: ${new Date().toLocaleString("es-CO", { dateStyle: "short", timeStyle: "short" })}.`
            : "Gestiona las alcaldías cliente y la operación cross-tenant desde aquí."}
        </p>

        {user && !user.totp_enabled && (
          <div className="alert warn" style={{ maxWidth: 720 }}>
            Tu cuenta no tiene 2FA activado.{" "}
            <Link to="/security" style={{ color: "inherit", fontWeight: 600 }}>
              Actívalo ahora →
            </Link>
          </div>
        )}

        {/* Filtros */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
            gap: 14,
            marginTop: 24,
            marginBottom: 22,
          }}
        >
          <CustomSelect value={tenantId} onChange={(v) => setTenantId(v)} options={tenantOpts} aria-label="Alcaldía" />
          <CustomSelect value={estado} onChange={(v) => setEstado(v as TenantStatus | "")} options={estadoOpts} aria-label="Estado" />
          {filtrosActivos && (
            <button
              type="button"
              className="cta ghost"
              style={{ width: "auto" }}
              onClick={() => {
                setEstado("");
                setTenantId("");
              }}
            >
              <span className="cta-inner">Limpiar filtros</span>
            </button>
          )}
        </div>

        {error && <div className="alert err">{error}</div>}

        {loading || !data ? (
          <p style={{ color: "var(--ink-3)" }}>Cargando panorama…</p>
        ) : (
          <>
            {/* KPIs principales */}
            <div className="kpi-strip">
              <Kpi label="Alcaldías" value={data.tenants_total} hint={renderTenantBreakdown(data.tenants_por_estado)} />
              <Kpi label="Funcionarios activos" value={data.funcionarios_activos} hint={`${data.funcionarios_total} en total`} />
              <Kpi label={`PQRSDF · ${fmtMes(data.mes_actual)}`} value={data.pqrsdf_mes_actual} hint={`${data.pqrsdf_ultimos_7d} en los últimos 7 días`} />
              <Kpi
                label="Cumplimiento promedio"
                value={data.cumplimiento_promedio_pct !== null ? `${data.cumplimiento_promedio_pct}%` : "—"}
                valueColor={
                  data.cumplimiento_promedio_pct === null
                    ? "var(--ink-4)"
                    : data.cumplimiento_promedio_pct >= 92
                      ? "var(--ok)"
                      : data.cumplimiento_promedio_pct >= 82
                        ? "var(--warn)"
                        : "var(--err)"
                }
                hint="Meta institucional ≥ 92%"
              />
              <Kpi
                label="Vencidos abiertos"
                value={data.pqrsdf_vencidos_abiertos}
                valueColor={data.pqrsdf_vencidos_abiertos > 0 ? "var(--err)" : undefined}
                hint={data.pqrsdf_vencidos_abiertos > 0 ? "Riesgo de sanción legal" : "Sin atrasos"}
              />
              <Kpi
                label="Backend"
                value={data.backend_status === "operativo" ? "Operativo" : data.backend_status}
                valueColor={data.backend_status === "operativo" ? "var(--ok)" : "var(--err)"}
                hint="api.mipuebloapp.com · postgres 16 · RLS activa"
              />
            </div>

            {/* Distribuciones + trend */}
            <div className="panel-row" style={{ marginTop: 32 }}>
              <section className="panel-block">
                <header>
                  <div className="eyebrow">Tendencia</div>
                  <h2>Radicados · últimos 30 días</h2>
                </header>
                <BarChart days={data.trend_30d} max={trendMax} />
              </section>
              <section className="panel-block">
                <header>
                  <div className="eyebrow">Distribución</div>
                  <h2>Por departamento</h2>
                </header>
                <ListBreakdown data={data.tenants_por_departamento} total={data.tenants_total} suffix="alcaldías" max={8} />
              </section>
            </div>

            <div className="panel-row" style={{ marginTop: 24 }}>
              <section className="panel-block">
                <header>
                  <div className="eyebrow">Operación</div>
                  <h2>Top alcaldías por volumen</h2>
                </header>
                <TopTenantsTable rows={data.top_tenants} />
              </section>
            </div>

            <div style={{ marginTop: 32 }}>
              <Link
                to="/tenants"
                className="cta ghost"
                style={{ maxWidth: 240, display: "inline-block" }}
              >
                <span className="cta-inner">Gestionar alcaldías →</span>
              </Link>
            </div>
          </>
        )}
      </section>
    </Shell>
  );
}

function renderTenantBreakdown(byEstado: Record<string, number>): string {
  const parts: string[] = [];
  for (const k of ["active", "trial", "suspended", "cancelled"]) {
    const v = byEstado[k];
    if (v) parts.push(`${v} ${STATUS_LABEL[k]?.toLowerCase()}`);
  }
  return parts.join(" · ") || "—";
}

function Kpi({
  label,
  value,
  valueColor,
  hint,
}: {
  label: string;
  value: string | number;
  valueColor?: string;
  hint: string;
}) {
  return (
    <div className="panel-kpi">
      <div className="lbl">{label}</div>
      <div className="val" style={{ color: valueColor }}>{value}</div>
      <div className="hint">{hint}</div>
    </div>
  );
}

function ListBreakdown({
  data,
  total,
  suffix,
  max,
}: {
  data: Record<string, number>;
  total: number;
  suffix: string;
  max: number;
}) {
  const entries = Object.entries(data).sort((a, b) => b[1] - a[1]).slice(0, max);
  if (entries.length === 0) {
    return <p style={{ color: "var(--ink-3)", padding: 20, textAlign: "center" }}>Sin datos.</p>;
  }
  return (
    <ul className="panel-breakdown">
      {entries.map(([k, n]) => {
        const pct = total > 0 ? Math.round((n / total) * 100) : 0;
        return (
          <li key={k}>
            <span className="name">{k}</span>
            <span className="bar">
              <span style={{ width: `${pct}%` }} />
            </span>
            <span className="n">
              {n} <span style={{ color: "var(--ink-3)" }}>· {pct}%</span>
            </span>
          </li>
        );
      })}
      {total > entries.reduce((acc, [, n]) => acc + n, 0) && (
        <li style={{ color: "var(--ink-3)", fontSize: 12 }}>
          <span className="name">… otros</span>
          <span />
          <span className="n">
            {total - entries.reduce((acc, [, n]) => acc + n, 0)} {suffix}
          </span>
        </li>
      )}
    </ul>
  );
}

function BarChart({ days, max }: { days: PanoramaTrendDay[]; max: number }) {
  return (
    <>
      <div className="panel-bars">
        {days.map((d) => {
          const h = (d.radicados / max) * 180;
          return (
            <div
              key={d.fecha}
              className="bar"
              style={{ height: h > 0 ? `${h}px` : "1px" }}
              title={`${d.fecha}: ${d.radicados}`}
            />
          );
        })}
      </div>
      <div className="panel-bar-labels">
        <span>{days[0]?.fecha.slice(5) ?? ""}</span>
        <span>{days[Math.floor(days.length / 2)]?.fecha.slice(5) ?? ""}</span>
        <span>{days[days.length - 1]?.fecha.slice(5) ?? ""}</span>
      </div>
    </>
  );
}

function TopTenantsTable({ rows }: { rows: PanoramaTopTenant[] }) {
  if (rows.length === 0) {
    return <p style={{ color: "var(--ink-3)", padding: 20, textAlign: "center" }}>Sin alcaldías con radicados todavía.</p>;
  }
  return (
    <table className="panel-top">
      <thead>
        <tr>
          <th>Alcaldía</th>
          <th className="right">Radicados</th>
          <th className="right">Cumplimiento</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((t) => (
          <tr key={t.tenant_id}>
            <td>
              <Link to={`/tenants/${t.tenant_id}`}>{t.nombre}</Link>
              <small>{t.departamento}</small>
            </td>
            <td className="right num">{t.radicados}</td>
            <td className={`right num ${complianceCls(t.cumplimiento_pct)}`}>
              {t.cumplimiento_pct !== null ? `${Math.round(t.cumplimiento_pct)}%` : "—"}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
